/**
 * <bundle-builder> Custom Element
 *
 * Features:
 * - Tier switching with per-tier selection memory (in-page only)
 * - Pre-fetched discount codes for fast checkout
 * - Cart cleanup on abandon (Razorpay dismiss + page reload)
 * - Razorpay Magic Checkout compatible
 */

if (!customElements.get('bundle-builder')) {
  class BundleBuilder extends HTMLElement {
    // ── Static: coordinate sticky bars across multiple instances ──
    static _instances = new Set();
    static _coordinationActive = false;
    static _activeBarOwner = null;

    constructor() {
      super();

      // ── State ──────────────────────────────────────────────────────
      this.activeTierIndex = 0;
      this.tierSelections = {};       // { [tierIndex]: { products: [...], gifts: [...] } }
      this.selectedProducts = [];     // current tier's products
      this.selectedGifts = [];        // current tier's gifts
      this.isExpanded = false;
      this.isProcessing = false;
      this.abandonWatcher = null;
      this.prefetchedDiscount = null; // { code, fingerprint, timestamp }
      this.storageKey = null;
      this.pendingCheckoutKey = null;
      this.headerObserver = null;
      this.onPageShow = this.onPageShow.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.updateMobileStickyOffset = this.updateMobileStickyOffset.bind(this);
    }

    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-bb-config]').textContent);
      this.tiers = this.config.tiers;
      this.products = this.config.products;
      this.workerUrl = this.config.workerUrl;
      this.moneyFormat = this.config.moneyFormat;
      this.cartAddUrl = this.config.cartAddUrl || '/cart/add.js';
      this.storageKey = `bundleBuilderState:${this.config.sectionId}:${window.location.pathname}`;
      this.pendingCheckoutKey = `pendingBundleCheckout:${this.config.sectionId}`;

      this.cacheElements();
      this.bindEvents();
      this.restorePersistedState();
      this.syncTierUI();
      this.setLoading(false);

      // Register with multi-instance coordinator
      BundleBuilder._instances.add(this);
      if (BundleBuilder._instances.size > 1) {
        BundleBuilder._setupCoordination();
      } else {
        this.stickyBar.setAttribute('data-visible', '');
      }

      // Clean up leftover bundle items from abandoned checkout
      this.cleanupAbandonedBundle();
      this.initHeaderOffsetTracking();
    }

    disconnectedCallback() {
      if (this.abandonWatcher) {
        clearInterval(this.abandonWatcher);
        this.abandonWatcher = null;
      }

      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('pagehide', this.onPageHide);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.updateMobileStickyOffset);
      window.removeEventListener('scroll', this.updateMobileStickyOffset);

      if (this.headerObserver) {
        this.headerObserver.disconnect();
        this.headerObserver = null;
      }

      if (this._onQuickViewLoaded) {
        document.removeEventListener('quickview:loaded', this._onQuickViewLoaded);
      }

      if (this._onQuickViewHide && this._quickViewId) {
        const qvEl = document.getElementById(this._quickViewId);
        if (qvEl) qvEl.removeEventListener('modal:afterHide', this._onQuickViewHide);
      }

      BundleBuilder._instances.delete(this);
      if (BundleBuilder._activeBarOwner === this) {
        BundleBuilder._activeBarOwner = null;
        if (BundleBuilder._coordinationActive) {
          BundleBuilder._updateActiveBar();
        }
      }
    }

    // ── Element caching ────────────────────────────────────────────

    cacheElements() {
      this.tierTabs = this.querySelectorAll('.bb-tier-tab');
      this.productGrid = this.querySelector('[data-bb-product-grid]');
      this.productCards = this.querySelectorAll('.bb-product-card:not([data-bb-gift])');
      this.stickyBar = this.querySelector('[data-bb-sticky-bar]');
      this.notificationEl = this.querySelector('[data-bb-notification]');
      this.thumbnailsEl = this.querySelector('[data-bb-thumbnails]');
      this.counterEl = this.querySelector('[data-bb-counter]');
      this.counterSecondaryEl = this.querySelector('[data-bb-counter-secondary]');
      this.bundlePriceEl = this.querySelector('[data-bb-bundle-price]');
      this.originalPriceEl = this.querySelector('[data-bb-original-price]');
      this.discountBadgeEl = this.querySelector('[data-bb-discount-badge]');
      this.expandedListEl = this.querySelector('[data-bb-expanded-list]');
      this.buyBtn = this.querySelector('[data-bb-buy-btn]');
      this.errorEl = this.querySelector('[data-bb-error]');

      // Gift picker
      this.giftOverlay = this.querySelector('[data-bb-gift-overlay]');
      this.giftSheet = this.querySelector('[data-bb-gift-sheet]');
      this.giftGrid = this.querySelector('[data-bb-gift-grid]');
      this.giftCloseBtn = this.querySelector('[data-bb-gift-close]');
      this.giftDoneBtn = this.querySelector('[data-bb-gift-done]');

      // Checkout overlay
      this.checkoutOverlay = this.querySelector('[data-bb-checkout-overlay]');
      this.checkoutStatusEl = this.querySelector('[data-bb-checkout-status]');

      // Quick view (shared per section)
      const qvEl = this.querySelector('[data-bundle-quickview]');
      this._quickViewId = qvEl ? qvEl.id : null;
      this._quickViewProductId = null;
    }

    // ── Event binding ──────────────────────────────────────────────

    bindEvents() {
      this.tierTabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const index = parseInt(tab.getAttribute('data-bb-tier-index'));
          this.setActiveTier(index);
        });
      });

      this.productCards.forEach((card) => {
        const addBtn = card.querySelector('[data-bb-add-btn]');
        if (addBtn) {
          addBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.incrementProduct(card);
          });
        }

        const minusBtn = card.querySelector('[data-bb-qty-minus]');
        if (minusBtn) {
          minusBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.decrementProduct(card);
          });
        }

        const plusBtn = card.querySelector('[data-bb-qty-plus]');
        if (plusBtn) {
          plusBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.incrementProduct(card);
          });
        }

        // Quick view: clicking image area opens product details modal
        const trigger = card.querySelector('[data-bb-quickview-trigger]');
        if (trigger) {
          trigger.addEventListener('click', (e) => {
            if (e.target.closest('[data-bb-add-btn]') || e.target.closest('[data-bb-qty-selector]')) return;
            this.openQuickView(card);
          });
        }
      });

      // Listen for quickview content loaded to inject "Add to Bundle" button
      this._onQuickViewLoaded = (e) => this.onQuickViewLoaded(e);
      document.addEventListener('quickview:loaded', this._onQuickViewLoaded);

      // Restore sticky bar when quickview closes
      if (this._quickViewId) {
        this._onQuickViewHide = () => {
          this.stickyBar.classList.remove('bb-sticky-bar--qv-hidden');
        };
        const qvEl = document.getElementById(this._quickViewId) || this.querySelector('[data-bundle-quickview]');
        if (qvEl) {
          qvEl.addEventListener('modal:afterHide', this._onQuickViewHide);
        }
      }

      this.querySelectorAll('[data-bb-toggle-expand]').forEach((toggleBtn) => {
        toggleBtn.addEventListener('click', () => this.toggleExpanded());
        if (toggleBtn.tagName !== 'BUTTON') {
          toggleBtn.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.toggleExpanded();
            }
          });
        }
      });

      if (this.buyBtn) {
        this.buyBtn.addEventListener('click', () => this.handleBuyBundle());
      }

      if (this.giftCloseBtn) {
        this.giftCloseBtn.addEventListener('click', () => this.hideGiftPicker());
      }
      if (this.giftOverlay) {
        this.giftOverlay.addEventListener('click', () => this.hideGiftPicker());
      }
      if (this.giftDoneBtn) {
        this.giftDoneBtn.addEventListener('click', () => this.hideGiftPicker());
      }

      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('pagehide', this.onPageHide);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.updateMobileStickyOffset, { passive: true });
      window.addEventListener('scroll', this.updateMobileStickyOffset, { passive: true });
    }

    // ── Tier management ────────────────────────────────────────────

    get activeTier() {
      return this.tiers[this.activeTierIndex] || this.tiers[0];
    }

    setActiveTier(index) {
      // Save current tier's selections before switching
      this._saveTierToMemory();

      this.activeTierIndex = index;
      this.isExpanded = false;
      this.prefetchedDiscount = null;

      // Restore target tier's selections
      this._restoreTierFromMemory();
      this.syncTierUI();
    }

    /**
     * Sync all visual state to match current tier + selections.
     * Called on init and on tier switch.
     */
    syncTierUI() {
      // Tab highlights
      this.tierTabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === this.activeTierIndex ? 'true' : 'false');
      });

      // Product card selection visuals + qty UI
      this.productCards.forEach((card) => {
        this._updateCardQtyUI(card);
      });

      this.syncAutoGifts();
      this.updateStickyBar();
      this.hideError();
      this.hideGiftPicker();
    }

    _saveTierToMemory() {
      this.tierSelections[this.activeTierIndex] = {
        productIds: this.selectedProducts.map((p) => p.id),
        giftIds: this.selectedGifts.map((g) => g.id),
      };
    }

    _restoreTierFromMemory() {
      const saved = this.tierSelections[this.activeTierIndex];
      if (!saved) {
        this.selectedProducts = [];
        this.selectedGifts = [];
        return;
      }

      const productIds = Array.isArray(saved.productIds) ? saved.productIds : [];
      const giftIds = Array.isArray(saved.giftIds) ? saved.giftIds : [];

      this.selectedProducts = productIds
        .map((id) => this.products.find((p) => p.id === id))
        .filter(Boolean)
        .map((p) => ({
          id: p.id,
          variantId: p.variantId,
          title: p.title,
          price: p.price,
          compareAtPrice: p.compareAtPrice || 0,
          image: p.image,
        }));

      const giftPool = this.activeTier.giftProducts || [];
      this.selectedGifts = giftIds
        .map((id) => giftPool.find((g) => g.id === id))
        .filter(Boolean)
        .map((g) => ({ id: g.id, variantId: g.variantId, title: g.title, price: 0, image: g.image }));
    }

    persistState() {
      if (!this.storageKey) return;

      this._saveTierToMemory();

      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            activeTierIndex: this.activeTierIndex,
            tierSelections: this.tierSelections,
            timestamp: Date.now(),
          })
        );
      } catch (err) {
        // Ignore storage failures silently
      }
    }

    restorePersistedState() {
      if (!this.storageKey) return;

      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (!raw) return;

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return;

        if (Number.isInteger(parsed.activeTierIndex) && parsed.activeTierIndex >= 0 && parsed.activeTierIndex < this.tiers.length) {
          this.activeTierIndex = parsed.activeTierIndex;
        }

        if (parsed.tierSelections && typeof parsed.tierSelections === 'object') {
          this.tierSelections = parsed.tierSelections;
        }

        this._restoreTierFromMemory();
      } catch (err) {
        sessionStorage.removeItem(this.storageKey);
      }
    }

    // ── Product selection ──────────────────────────────────────────

    getProductQty(productId) {
      return this.selectedProducts.filter((p) => p.id === productId).length;
    }

    _updateCardQtyUI(card) {
      const productId = parseInt(card.getAttribute('data-bb-product-id'));
      const qty = this.getProductQty(productId);
      const addBtn = card.querySelector('[data-bb-add-btn]');
      const qtySelector = card.querySelector('[data-bb-qty-selector]');
      const qtyValue = card.querySelector('[data-bb-qty-value]');
      const plusBtn = card.querySelector('[data-bb-qty-plus]');

      if (qty > 0) {
        card.setAttribute('data-bb-selected', '');
        if (addBtn) addBtn.style.display = 'none';
        if (qtySelector) {
          qtySelector.setAttribute('data-visible', '');
          qtySelector.removeAttribute('aria-hidden');
        }
        if (qtyValue) qtyValue.textContent = qty;
        if (plusBtn) plusBtn.disabled = this.selectedProducts.length >= this.activeTier.productCount;
      } else {
        card.removeAttribute('data-bb-selected');
        if (addBtn) addBtn.style.display = '';
        if (qtySelector) {
          qtySelector.removeAttribute('data-visible');
          qtySelector.setAttribute('aria-hidden', 'true');
        }
      }
    }

    incrementProduct(card) {
      const productId = parseInt(card.getAttribute('data-bb-product-id'));
      if (this.selectedProducts.length >= this.activeTier.productCount) return;

      const product = this.products.find((p) => p.id === productId);
      if (!product || !product.available) return;

      this.selectedProducts.push({
        id: product.id,
        variantId: product.variantId,
        title: product.title,
        price: product.price,
        compareAtPrice: product.compareAtPrice || 0,
        image: product.image,
      });

      this.prefetchedDiscount = null;
      this._updateCardQtyUI(card);
      // Update plus buttons on all cards (slot count changed)
      this.productCards.forEach((c) => {
        const plus = c.querySelector('[data-bb-qty-plus]');
        if (plus) plus.disabled = this.selectedProducts.length >= this.activeTier.productCount;
      });
      this.maybePrefetchDiscount();
      this.syncAutoGifts();
      this.updateStickyBar();
      this.hideError();
    }

    decrementProduct(card) {
      const productId = parseInt(card.getAttribute('data-bb-product-id'));
      if (this.getProductQty(productId) <= 0) return;

      // Remove the last occurrence of this product
      const lastIdx = this.selectedProducts.reduceRight(
        (found, p, i) => (found === -1 && p.id === productId ? i : found),
        -1
      );
      if (lastIdx !== -1) this.selectedProducts.splice(lastIdx, 1);

      this.prefetchedDiscount = null;
      this._updateCardQtyUI(card);
      // Re-enable plus buttons on all cards (a slot just freed up)
      this.productCards.forEach((c) => {
        const plus = c.querySelector('[data-bb-qty-plus]');
        if (plus) plus.disabled = this.selectedProducts.length >= this.activeTier.productCount;
      });
      this.syncAutoGifts();
      this.updateStickyBar();
      this.hideError();
    }

    removeProduct(productId) {
      this.selectedProducts = this.selectedProducts.filter((p) => p.id !== productId);

      const card = this.querySelector(
        `.bb-product-card[data-bb-product-id="${productId}"]:not([data-bb-gift])`
      );
      if (card) this._updateCardQtyUI(card);

      // Re-enable plus buttons on all cards (slots freed up)
      this.productCards.forEach((c) => {
        const plus = c.querySelector('[data-bb-qty-plus]');
        if (plus) plus.disabled = this.selectedProducts.length >= this.activeTier.productCount;
      });

      this.prefetchedDiscount = null;
      this.syncAutoGifts();
      this.updateStickyBar();
    }

    // ── Gift selection ─────────────────────────────────────────────

    getAvailableGifts() {
      return (this.activeTier.giftProducts || []).filter((g) => g.available);
    }

    getRequiredGiftCount() {
      const desired = this.activeTier.freeGiftCount || 0;
      if (desired <= 0) return 0;
      return Math.min(desired, this.getAvailableGifts().length);
    }

    syncAutoGifts() {
      const productsComplete = this.selectedProducts.length === this.activeTier.productCount;
      const requiredGiftCount = this.getRequiredGiftCount();

      if (!productsComplete || requiredGiftCount === 0) {
        this.selectedGifts = [];
        this.hideGiftPicker();
        return;
      }

      const availableGifts = this.getAvailableGifts();
      const selectedSet = new Set(this.selectedGifts.map((gift) => gift.id));

      const nextGifts = this.selectedGifts
        .filter((gift) => availableGifts.some((g) => g.id === gift.id))
        .slice(0, requiredGiftCount);

      for (const gift of availableGifts) {
        if (nextGifts.length >= requiredGiftCount) break;
        if (selectedSet.has(gift.id)) continue;
        nextGifts.push({
          id: gift.id,
          variantId: gift.variantId,
          title: gift.title,
          price: 0,
          image: gift.image,
        });
      }

      this.selectedGifts = nextGifts;
      this.hideGiftPicker();
    }

    showGiftPicker() {
      const tier = this.activeTier;
      if (!tier.giftProducts || tier.giftProducts.length === 0) return;

      this.giftGrid.innerHTML = tier.giftProducts
        .filter((g) => g.available)
        .map(
          (gift) => `
          <div class="bb-product-card flex flex-col leading-none relative"
               data-bb-gift
               data-bb-gift-id="${gift.id}"
               data-bb-gift-variant-id="${gift.variantId}"
               data-bb-gift-price="${gift.price}"
               ${this.selectedGifts.some((g) => g.id === gift.id) ? 'data-bb-selected' : ''}>
            <div class="bb-product-card__check" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="12" fill="currentColor"/>
                <path d="M7 12.5L10.5 16L17 9" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="product-card__media relative h-auto overflow-hidden">
              <div class="block relative media media--portrait overflow-hidden">
                <img src="${gift.image}" alt="${this.escapeHtml(gift.title)}" loading="lazy" />
              </div>
            </div>
            <div class="product-card__content grow flex flex-col justify-start">
              <p class="bb-product-card__title text-base font-medium leading-tight line-clamp-2">${this.escapeHtml(gift.title)}</p>
              <div class="price text-sm"><span class="price__regular">FREE</span></div>
            </div>
          </div>
        `
        )
        .join('');

      this.giftGrid.querySelectorAll('[data-bb-gift]').forEach((giftCard) => {
        giftCard.addEventListener('click', () => this.handleGiftClick(giftCard));
      });

      this.giftOverlay.setAttribute('data-visible', '');
      this.giftSheet.setAttribute('data-visible', '');
    }

    hideGiftPicker() {
      if (this.giftOverlay) this.giftOverlay.removeAttribute('data-visible');
      if (this.giftSheet) this.giftSheet.removeAttribute('data-visible');
    }

    handleGiftClick(giftCard) {
      const giftId = parseInt(giftCard.getAttribute('data-bb-gift-id'));
      const isSelected = giftCard.hasAttribute('data-bb-selected');

      if (isSelected) {
        this.selectedGifts = this.selectedGifts.filter((g) => g.id !== giftId);
        giftCard.removeAttribute('data-bb-selected');
      } else {
        if (this.selectedGifts.length >= this.activeTier.freeGiftCount) return;

        const giftData = this.activeTier.giftProducts.find((g) => g.id === giftId);
        if (!giftData) return;

        this.selectedGifts.push({
          id: giftData.id,
          variantId: giftData.variantId,
          title: giftData.title,
          price: giftData.price,
          image: giftData.image,
        });
        giftCard.setAttribute('data-bb-selected', '');
      }

      this.prefetchedDiscount = null;
      this.updateStickyBar();
      this.maybePrefetchDiscount();
    }

    // ── Quick View ────────────────────────────────────────────────

    openQuickView(card) {
      const productUrl = card.getAttribute('data-bb-product-url');
      if (!productUrl || !this._quickViewId) return;

      const qv = document.getElementById(this._quickViewId);
      if (!qv) return;

      this._quickViewProductId = parseInt(card.getAttribute('data-bb-product-id'));
      qv.setAttribute('data-product-url', productUrl);
      // Hide sticky bar while quickview is open
      this.stickyBar.classList.add('bb-sticky-bar--qv-hidden');
      qv.show();
    }

    onQuickViewLoaded() {
      const qv = document.getElementById(this._quickViewId);
      if (!qv || !qv.hasAttribute('open') || !qv.hasAttribute('data-bundle-quickview')) return;

      const content = qv.querySelector('.quick-view__content');
      if (!content) return;

      const productId = this._quickViewProductId;

      // Hide native buy controls, dynamic checkout, quantity, and "View full details" link
      const hideSelectors = [
        '.buy-buttons',
        '.shopify-payment-button',
        '[data-shopify="payment-button"]',
        'quantity-input.quantity',
        '.product__more',
        '.product__pickup-availabilities',
      ];
      hideSelectors.forEach((sel) => {
        content.querySelectorAll(sel).forEach((el) => {
          el.style.display = 'none';
        });
      });

      // Build "Add to Bundle" button
      const isSelected = this.selectedProducts.some((p) => p.id === productId);
      const isAtLimit = this.selectedProducts.length >= this.activeTier.productCount;

      const bundleBtn = document.createElement('button');
      bundleBtn.type = 'button';
      bundleBtn.className = 'bb-qv-add-btn';

      if (isSelected) {
        bundleBtn.textContent = 'Remove from Bundle';
      } else if (isAtLimit) {
        bundleBtn.textContent = `Bundle Full (${this.activeTier.productCount}/${this.activeTier.productCount})`;
        bundleBtn.disabled = true;
      } else {
        bundleBtn.textContent = 'Add to Bundle';
      }

      bundleBtn.addEventListener('click', () => {
        const card = this.querySelector(
          `.bb-product-card[data-bb-product-id="${productId}"]:not([data-bb-gift])`
        );
        if (card) this.handleProductClick(card);
        qv.hide();
      });

      // Insert the button after the hidden buy-buttons section
      const productInfo = content.querySelector('.product__info') || content.querySelector('product-info');
      const buyButtons = content.querySelector('.buy-buttons');
      if (productInfo && buyButtons) {
        buyButtons.parentNode.insertBefore(bundleBtn, buyButtons.nextSibling);
      } else if (productInfo) {
        productInfo.appendChild(bundleBtn);
      } else {
        content.appendChild(bundleBtn);
      }
    }

    // ── Sticky bar updates ─────────────────────────────────────────

    updateStickyBar() {
      const tier = this.activeTier;
      const count = this.selectedProducts.length;
      const total = tier.productCount;

      // Show bar (skipped when multi-instance coordination manages visibility)
      if (!BundleBuilder._coordinationActive) {
        this.stickyBar.setAttribute('data-visible', '');
      }

      if (count === 0) {
        this.stickyBar.removeAttribute('data-expanded');
        this.isExpanded = false;
      }

      // Counter
      this.counterEl.textContent = `${count}/${total} Products`;
      if (this.counterSecondaryEl) {
        this.counterSecondaryEl.textContent = `${count}/${total} Products`;
      }

      this.renderThumbnails();
      this.updatePrices();
      this.updateNotification();
      this.renderExpandedList();
      this.updateBuyButton();
      this.persistState();
    }

    renderThumbnails() {
      const total = this.activeTier.productCount;
      let html = '';

      for (let i = 0; i < total; i++) {
        const product = this.selectedProducts[i];
        if (product) {
          html += `<div class="bb-thumbnail"><img src="${product.image}" alt="${this.escapeHtml(product.title)}" /></div>`;
        } else {
          html += `<div class="bb-thumbnail bb-thumbnail--empty"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="6" x2="12" y2="18"/><line x1="6" y1="12" x2="18" y2="12"/></svg></div>`;
        }
      }

      this.thumbnailsEl.innerHTML = html;
    }

    updatePrices() {
      const tier = this.activeTier;
      const count = this.selectedProducts.length;
      const selectedTotal = this.selectedProducts.reduce((sum, p) => sum + p.price, 0);
      const selectedCompareAtTotal = this.selectedProducts.reduce(
        (sum, p) => sum + (p.compareAtPrice && p.compareAtPrice > p.price ? p.compareAtPrice : p.price),
        0
      );
      const isComplete = count === tier.productCount;

      if (count === 0) {
        this.bundlePriceEl.textContent = this.formatMoney(0);
        this.originalPriceEl.textContent = '';
        this.discountBadgeEl.style.display = 'none';
        return;
      }

      if (!isComplete) {
        this.bundlePriceEl.textContent = this.formatMoney(selectedTotal);
        this.originalPriceEl.textContent =
          selectedCompareAtTotal > selectedTotal ? this.formatMoney(selectedCompareAtTotal) : '';
        this.discountBadgeEl.style.display = 'none';
      } else {
        this.bundlePriceEl.textContent = this.formatMoney(tier.bundlePrice);

        if (selectedTotal > tier.bundlePrice) {
          this.originalPriceEl.textContent = this.formatMoney(selectedTotal);
          const discountPct = Math.round(((selectedTotal - tier.bundlePrice) / selectedTotal) * 100);
          this.discountBadgeEl.textContent = `${discountPct}% OFF`;
          this.discountBadgeEl.style.display = '';
        } else {
          this.originalPriceEl.textContent = '';
          this.discountBadgeEl.style.display = 'none';
        }
      }
    }

    updateNotification() {
      const tier = this.activeTier;
      const remaining = tier.productCount - this.selectedProducts.length;

      if (remaining > 0) {
        this.notificationEl.textContent = `Add ${remaining} more product${remaining > 1 ? 's' : ''} to get the bundle at ${this.formatMoney(tier.bundlePrice)}`;
        this.notificationEl.setAttribute('data-visible', '');
      } else {
        this.notificationEl.textContent = `🎉 Best Deal Unlocked! Get ${tier.productCount} products at ${this.formatMoney(tier.bundlePrice)}`;
        this.notificationEl.setAttribute('data-visible', '');
      }
    }

    renderExpandedList() {
      // Group products by id so duplicates show as qty > 1
      const productMap = {};
      this.selectedProducts.forEach((p) => {
        if (!productMap[p.id]) productMap[p.id] = { ...p, qty: 0 };
        productMap[p.id].qty++;
      });

      const allItems = [
        ...Object.values(productMap).map((p) => ({ ...p, isGift: false })),
        ...this.selectedGifts.map((g) => ({ ...g, isGift: true, qty: 1 })),
      ];

      if (allItems.length === 0) {
        this.expandedListEl.innerHTML = '';
        return;
      }

      this.expandedListEl.innerHTML = allItems
        .map(
          (item) => `
          <div class="bb-expanded-item">
            <div class="bb-expanded-item__img"><img src="${item.image}" alt="${this.escapeHtml(item.title)}" /></div>
            <div class="bb-expanded-item__meta">
              <span class="bb-expanded-item__title">${this.escapeHtml(item.title)}${item.isGift ? ' (Gift)' : ''}</span>
              <div class="bb-expanded-item__line">
                <span class="bb-expanded-item__price${item.isGift ? ' bb-expanded-item__price--gift' : ''}">${item.isGift ? 'FREE' : this.formatMoney(item.price)}</span>
                <span class="bb-expanded-item__qty">x${item.qty}</span>
              </div>
            </div>
            ${
              item.isGift
                ? '<span class="bb-expanded-item__gift-tag">FREE GIFT</span>'
                : `<button type="button" class="bb-expanded-item__remove" data-remove-id="${item.id}" data-remove-gift="${item.isGift}" aria-label="Remove item">
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 7H20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                      <path d="M9 4H15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                      <rect x="6" y="7" width="12" height="14" rx="2" stroke="currentColor" stroke-width="2"/>
                      <path d="M10 11V17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                      <path d="M14 11V17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>`
            }
          </div>
        `
        )
        .join('');

      this.expandedListEl.querySelectorAll('.bb-expanded-item__remove').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const id = parseInt(btn.getAttribute('data-remove-id'));
          const isGift = btn.getAttribute('data-remove-gift') === 'true';
          if (isGift) return;
          this.removeProduct(id);
          this.updateStickyBar();
        });
      });

    }

    updateBuyButton() {
      const tier = this.activeTier;
      const productsComplete = this.selectedProducts.length === tier.productCount;
      const giftsComplete = this.selectedGifts.length >= this.getRequiredGiftCount();

      const isReady = productsComplete && giftsComplete && !this.isProcessing;
      this.buyBtn.disabled = !isReady;

      const textEl = this.buyBtn.querySelector('.bb-buy-btn__text');
      textEl.textContent = 'BUY BUNDLE';
    }

    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.stickyBar.setAttribute('data-expanded', '');
      } else {
        this.stickyBar.removeAttribute('data-expanded');
      }
    }

    // ── Pre-fetch discount (speed optimization) ────────────────────
    //
    // Called when the selection is complete. Silently fetches a discount
    // code in the background so the Buy click is faster.

    async maybePrefetchDiscount() {
      const tier = this.activeTier;
      const productsComplete = this.selectedProducts.length === tier.productCount;
      const giftsComplete = this.selectedGifts.length >= this.getRequiredGiftCount();

      if (!productsComplete || !giftsComplete || !this.workerUrl) return;

      const allItems = this._buildItemList();
      const fingerprint = allItems.map((i) => i.variantId).sort().join(',');

      // Skip if already prefetched for these exact items and not expired
      if (
        this.prefetchedDiscount &&
        this.prefetchedDiscount.fingerprint === fingerprint &&
        Date.now() - this.prefetchedDiscount.timestamp < 8 * 60 * 1000
      ) {
        return;
      }

      this.prefetchedDiscount = null;

      try {
        const res = await fetch(this.workerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tierLabel: tier.label,
            items: allItems,
          }),
        });

        if (!res.ok) return;
        const data = await res.json();

        if (data.discountCode) {
          this.prefetchedDiscount = {
            code: data.discountCode,
            fingerprint,
            timestamp: Date.now(),
          };
        }
      } catch (e) {
        // Silently fail — will create on Buy click instead
      }
    }

    _buildItemList() {
      // Deduplicate products by variantId, summing quantities
      const productMap = new Map();
      this.selectedProducts.forEach((p) => {
        if (productMap.has(p.variantId)) {
          productMap.get(p.variantId).quantity++;
        } else {
          productMap.set(p.variantId, { variantId: p.variantId, quantity: 1, isGift: false });
        }
      });

      return [
        ...productMap.values(),
        ...this.selectedGifts.map((g) => ({ variantId: g.variantId, quantity: 1, isGift: true })),
      ];
    }

    // ── Buy Bundle flow ────────────────────────────────────────────

    async handleBuyBundle() {
      if (this.isProcessing) return;

      const tier = this.activeTier;
      const allItems = this._buildItemList();

      if (!this.workerUrl) {
        this.showError('Bundle checkout not configured.');
        return;
      }

      this.setLoading(true, 'Preparing your bundle...');
      this.hideError();

      // ── Clean up any leftover bundle items from a previous attempt ──
      try {
        const pendingRaw = sessionStorage.getItem(this.pendingCheckoutKey);
        if (pendingRaw) {
          const prev = JSON.parse(pendingRaw);
          if (prev && prev.bundleId) {
            await this.removeBundleItemsFromCart(prev.bundleId, prev.itemKeys || []);
            sessionStorage.removeItem(this.pendingCheckoutKey);
          }
        }
      } catch (err) {
        // ignore — proceed with fresh bundle
      }

      const bundleId = `bb-${Date.now()}`;
      let addedLineKeys = [];

      try {
        // ── Get discount code (prefetched or fresh) + add to cart ──
        let discountCode;
        const fingerprint = allItems.map((i) => i.variantId).sort().join(',');
        const hasPrefetch =
          this.prefetchedDiscount &&
          this.prefetchedDiscount.fingerprint === fingerprint &&
          Date.now() - this.prefetchedDiscount.timestamp < 8 * 60 * 1000;

        const cartItems = allItems.map((item) => ({
          id: item.variantId,
          quantity: item.quantity || 1,
          properties: {
            _bundle_id: bundleId,
            ...(item.isGift ? { _bundle_gift: 'true' } : {}),
          },
        }));

        if (hasPrefetch) {
          // Fast path: discount already created, just add to cart
          discountCode = this.prefetchedDiscount.code;
          const cartRes = await fetch(this.cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: cartItems }),
          });
          if (!cartRes.ok) throw new Error('Failed to add items to cart');
          const cartData = await cartRes.json().catch(() => null);
          addedLineKeys = this.extractAddedLineKeys(cartData, bundleId);
        } else {
          // No prefetch: worker + cart add in parallel
          this.setLoading(true, 'Adding items to your cart...');
          const [workerRes, cartRes] = await Promise.all([
            fetch(this.workerUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                tierLabel: tier.label,
                items: allItems,
              }),
            }),
            fetch(this.cartAddUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: cartItems }),
            }),
          ]);

          if (!workerRes.ok) {
            const err = await workerRes.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to create discount');
          }
          if (!cartRes.ok) throw new Error('Failed to add items to cart');

          const workerData = await workerRes.json();
          const cartData = await cartRes.json().catch(() => null);
          addedLineKeys = this.extractAddedLineKeys(cartData, bundleId);
          discountCode = workerData.discountCode;
          if (!discountCode) throw new Error('No discount code received');
        }

        // ── Apply discount code ──────────────────────────────────
        this.setLoading(true, 'Applying your discount...');
        await fetch(`/discount/${discountCode}?v=${Date.now()}&redirect=/`, {
          method: 'GET',
          credentials: 'same-origin',
        });

        // Store for cleanup
        sessionStorage.setItem(
          this.pendingCheckoutKey,
          JSON.stringify({
            bundleId,
            discountCode,
            itemCount: allItems.length,
            itemKeys: addedLineKeys,
            timestamp: Date.now(),
          })
        );

        // ── Trigger checkout ─────────────────────────────────────
        this.setLoading(true, 'Opening secure checkout...');
        const checkoutBtn =
          document.querySelector('form[action*="/cart"] button[name="checkout"]') ||
          document.querySelector('button[name="checkout"][type="submit"]') ||
          document.querySelector('[name="checkout"]');

        if (checkoutBtn) {
          checkoutBtn.click();
        } else {
          window.location.href = '/checkout';
        }

        // If checkout does not open (popup blocked / script issue), avoid spinner lock.
        // But don't re-enable if abandon watcher is still running (Razorpay may still open).
        window.setTimeout(() => {
          if (document.visibilityState === 'visible' && !this.abandonWatcher) {
            this.setLoading(false);
          }
        }, 4000);

        // Watch for Razorpay dismiss
        this.watchForAbandon(bundleId);
        this.prefetchedDiscount = null;
        this.persistState();

      } catch (err) {
        console.error('Bundle purchase error:', err);
        this.showError(err.message || 'Something went wrong. Please try again.');
        this.setLoading(false);
        this.removeBundleItemsFromCart(bundleId, addedLineKeys);
      }
    }

    // ── Abandon detection & cleanup ────────────────────────────────

    watchForAbandon(bundleId) {
      let razorpayAppeared = false;
      let abandonHandled = false;
      const startTime = Date.now();
      const maxWaitForAppear = 10000;

      if (this.abandonWatcher) clearInterval(this.abandonWatcher);

      const cleanup = () => {
        if (this.abandonWatcher) {
          clearInterval(this.abandonWatcher);
          this.abandonWatcher = null;
        }
        window.removeEventListener('focus', onFocus);
      };

      const triggerAbandon = () => {
        if (abandonHandled) return;
        abandonHandled = true;
        cleanup();
        this.handleCheckoutAbandon(bundleId);
      };

      // Primary signal: page regains focus after Razorpay modal is closed.
      // Razorpay Magic Checkout takes focus; closing it returns focus to page.
      const onFocus = () => {
        if (!razorpayAppeared) return;
        // Short delay to let Razorpay DOM settle after dismiss
        setTimeout(() => {
          if (!this._isRazorpayVisible()) {
            triggerAbandon();
          }
        }, 600);
      };

      // Attach focus listener after a short delay (let Razorpay open first)
      setTimeout(() => window.addEventListener('focus', onFocus), 2000);

      this.abandonWatcher = setInterval(() => {
        const rzpVisible = this._isRazorpayVisible();

        if (rzpVisible && !razorpayAppeared) razorpayAppeared = true;

        // Razorpay appeared then disappeared → abandoned
        if (razorpayAppeared && !rzpVisible) {
          triggerAbandon();
        }

        // Razorpay never appeared within timeout → still clean up
        if (!razorpayAppeared && Date.now() - startTime > maxWaitForAppear) {
          triggerAbandon();
        }
      }, 500);
    }

    /**
     * Detect Razorpay Magic Checkout modal visibility.
     * Checks that matching elements are actually visible on screen,
     * not just lingering hidden in the DOM after modal dismiss.
     */
    _isRazorpayVisible() {
      const candidates = document.querySelectorAll(
        '.razorpay-container, .razorpay-checkout-frame, .razorpay-backdrop, ' +
        'iframe[src*="razorpay"], iframe[src*="api.razorpay.com"], ' +
        '[id*="razorpay"]'
      );

      for (const el of candidates) {
        // Skip <script>, <link>, <style> — never visual
        const tag = el.tagName;
        if (tag === 'SCRIPT' || tag === 'LINK' || tag === 'STYLE') continue;

        // Must have actual screen dimensions to be truly visible
        if (el.offsetWidth > 0 && el.offsetHeight > 0) return true;
      }

      return false;
    }

    async handleCheckoutAbandon(bundleId) {
      // Keep button disabled while we clean up the cart
      this.setLoading(true, 'Cleaning up...');

      let itemKeys = [];
      try {
        const pendingRaw = sessionStorage.getItem(this.pendingCheckoutKey);
        if (pendingRaw) {
          const pending = JSON.parse(pendingRaw);
          if (pending && pending.bundleId === bundleId && Array.isArray(pending.itemKeys)) {
            itemKeys = pending.itemKeys;
          }
        }
      } catch (err) {
        // ignore parse/storage errors
      }

      // Wait for cart cleanup to finish before re-enabling the button
      await this.removeBundleItemsFromCart(bundleId, itemKeys);
      sessionStorage.removeItem(this.pendingCheckoutKey);
      this.setLoading(false);
    }

    extractAddedLineKeys(cartData, bundleId) {
      if (!cartData) return [];

      const items = Array.isArray(cartData)
        ? cartData
        : Array.isArray(cartData.items)
          ? cartData.items
          : [cartData];

      return items
        .filter((item) => item && item.key)
        .filter((item) => {
          const props = item.properties || {};
          return props._bundle_id === bundleId;
        })
        .map((item) => item.key);
    }

    async removeBundleItemsFromCart(bundleId, knownItemKeys = []) {
      try {
        const updates = {};
        let hasBundleItems = false;

        if (Array.isArray(knownItemKeys) && knownItemKeys.length > 0) {
          knownItemKeys.forEach((key) => {
            if (!key) return;
            updates[key] = 0;
            hasBundleItems = true;
          });
        }

        if (!hasBundleItems) {
          const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
          const cart = await cartRes.json();
          if (!cart.items || cart.items.length === 0) return;

          for (const item of cart.items) {
            const props = item.properties || {};
            if (props._bundle_id === bundleId) {
              updates[item.key] = 0;
              hasBundleItems = true;
            }
          }
        }

        if (!hasBundleItems) return;

        const updateRes = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
          credentials: 'same-origin',
        });

        // Update cart count bubble via theme's pubsub system
        const updatedCart = await updateRes.json().catch(() => null);
        if (updatedCart && typeof theme !== 'undefined' && theme.pubsub) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: updatedCart });
        }

        // Also refresh the cart drawer
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } catch (err) {
        console.error('Bundle cleanup error:', err);
      }
    }

    async cleanupAbandonedBundle() {
      try {
        const pending = sessionStorage.getItem(this.pendingCheckoutKey);
        if (!pending) return;

        const { bundleId, timestamp, itemKeys } = JSON.parse(pending);

        // Short grace to avoid racing with immediate checkout redirects.
        if (Date.now() - timestamp < 1200) return;

        await this.removeBundleItemsFromCart(bundleId, itemKeys);
        sessionStorage.removeItem(this.pendingCheckoutKey);
      } catch (err) {
        console.error('Abandoned bundle cleanup error:', err);
        sessionStorage.removeItem(this.pendingCheckoutKey);
      }
    }

    // ── UI helpers ─────────────────────────────────────────────────

    onPageShow() {
      this.setLoading(false);
      this.restorePersistedState();
      this.syncTierUI();
      this.updateMobileStickyOffset();
    }

    onPageHide() {
      this.setLoading(false);
    }

    onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      this.setLoading(false);
      this.updateMobileStickyOffset();
    }

    initHeaderOffsetTracking() {
      const headerSection = document.querySelector('.header-section');
      if (!headerSection) return;

      this.headerObserver = new MutationObserver(() => this.updateMobileStickyOffset());
      this.headerObserver.observe(headerSection, {
        attributes: true,
        attributeFilter: ['class'],
      });

      this.updateMobileStickyOffset();
    }

    getHeaderVisibleHeight() {
      if (window.matchMedia('(min-width: 990px)').matches) return 0;

      const headerSection = document.querySelector('.header-section');
      if (!headerSection || !headerSection.classList.contains('header-sticky')) return 0;
      if (headerSection.classList.contains('header-hidden')) return 0;

      const header = headerSection.querySelector('.header');
      if (header) {
        const rect = header.getBoundingClientRect();
        return Math.max(0, Math.round(rect.height));
      }

      const cssHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      return Number.isFinite(cssHeight) ? Math.max(0, Math.round(cssHeight)) : 0;
    }

    updateMobileStickyOffset() {
      this.style.setProperty('--bb-mobile-sticky-offset', `${this.getHeaderVisibleHeight()}px`);
    }

    // ── Multi-instance sticky bar coordination ──────────────────────

    static _setupCoordination() {
      if (BundleBuilder._coordinationActive) return;
      BundleBuilder._coordinationActive = true;

      // Take over visibility management from all existing instances
      for (const inst of BundleBuilder._instances) {
        inst.stickyBar.removeAttribute('data-visible');
      }
      BundleBuilder._activeBarOwner = null;
      BundleBuilder._updateActiveBar();

      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          BundleBuilder._updateActiveBar();
          ticking = false;
        });
      }, { passive: true });
    }

    static _updateActiveBar() {
      const viewCenter = window.innerHeight / 2;
      let best = null;
      let bestDist = Infinity;

      for (const inst of BundleBuilder._instances) {
        const rect = inst.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) continue;
        const dist = Math.abs(rect.top + rect.height / 2 - viewCenter);
        if (dist < bestDist) {
          bestDist = dist;
          best = inst;
        }
      }

      if (best === BundleBuilder._activeBarOwner) return;

      if (BundleBuilder._activeBarOwner) {
        BundleBuilder._activeBarOwner.stickyBar.removeAttribute('data-visible');
      }
      BundleBuilder._activeBarOwner = best;
      if (best) {
        best.stickyBar.setAttribute('data-visible', '');
      }
    }

    setLoading(loading, statusMessage = '') {
      this.isProcessing = loading;
      if (loading) {
        this.buyBtn.setAttribute('data-loading', '');
        this.buyBtn.disabled = true;
        this.showCheckoutOverlay(statusMessage);
      } else {
        this.buyBtn.removeAttribute('data-loading');
        this.hideCheckoutOverlay();
        this.updateBuyButton();
        this.updateNotification();
      }
    }

    showCheckoutOverlay(statusMessage = '') {
      if (!this.checkoutOverlay) return;
      if (statusMessage && this.checkoutStatusEl) {
        this.checkoutStatusEl.textContent = statusMessage;
      }
      this.checkoutOverlay.setAttribute('data-visible', '');
    }

    hideCheckoutOverlay() {
      if (!this.checkoutOverlay) return;
      this.checkoutOverlay.removeAttribute('data-visible');
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.setAttribute('data-visible', '');
    }

    hideError() {
      this.errorEl.textContent = '';
      this.errorEl.removeAttribute('data-visible');
    }

    formatMoney(cents) {
      if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, this.moneyFormat);
      }
      const amount = (cents / 100).toFixed(2);
      return this.moneyFormat
        ? this.moneyFormat
            .replace(/\{\{\s*amount\s*\}\}/, amount)
            .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        : `₹${Math.round(cents / 100)}`;
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
